import Button from "@/components/Button";
import {
  defaultPositions,
  getRolesPlusOrPlusPlus,
  playerRoleByPositionSelector,
} from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";
import { MetaRating } from "@repo/shared/types";
import { useSelector } from "react-redux";

import { GridBox } from "../DetailedPlayer";
import { Chemistry, DetailedPlayerAction } from "../reducer";
import { RoleSection, RoleWithBest } from "./RolesSection";

interface AllRolesSectionProps {
  handleShowAllRoles: () => void;
  chemistry: Chemistry;
  currentPlayerRoleId: number;
  possiblePositions: string[];
  metaRatingsForAllRoles: MetaRating[];
  rolesPlus: number[];
  handleAction: (action: DetailedPlayerAction) => void;
}

export const getBestMetaRatingAndRole = ({
  metaRatings,
  chemistry,
}: {
  metaRatings: MetaRating[];
  chemistry: Chemistry;
}): MetaRating => {
  let bestMetaRatingAndRole: MetaRating | null = null;
  metaRatings?.forEach((metaRating) => {
    if (metaRating.chemistry === chemistry) {
      if (metaRating.metaRating > (bestMetaRatingAndRole?.metaRating ?? 0)) {
        bestMetaRatingAndRole = metaRating;
      }
    }
  });

  return bestMetaRatingAndRole;
};

const RoleSectionWrapper = ({
  position,
  currentPlayerRoleId,
  handleAction,
  isDisabled,
  metaRatingsForAllRoles,
  rolesPlus,
  chemistry,
}: {
  position: string;
  currentPlayerRoleId: number;
  handleAction: (action: DetailedPlayerAction) => void;
  isDisabled: boolean;
  metaRatingsForAllRoles: MetaRating[];
  rolesPlus: number[];
  chemistry: Chemistry;
}) => {
  const roles = useSelector(playerRoleByPositionSelector({ position }));

  const bestRoleAndMetaRating = getBestMetaRatingAndRole({
    metaRatings: metaRatingsForAllRoles,
    chemistry,
  });
  let rolesWithMetaRating: RoleWithBest[] = [];
  roles.forEach((role) => {
    if (isDisabled) {
      rolesWithMetaRating.push({
        playerRoleId: role.id,
        position: undefined,
        metaRating: undefined,
        metaRatingZeroChem: undefined,
        plusPlus: undefined,
        plus: undefined,
        best: false,
        chemstyleId: 0,
      });
    } else {
      const isRolesPlusOrPlusPlus = getRolesPlusOrPlusPlus({
        playerRoleId: role.id,
        rolesPlus,
      });
      const metaRatingForRole = metaRatingsForAllRoles.find(
        (mr) => mr.playerRoleId === role.id && mr.chemistry === chemistry,
      );
      const metaRatingZeroChemForRole = metaRatingsForAllRoles.find(
        (mr) => mr.playerRoleId === role.id && mr.chemistry === 0,
      );

      rolesWithMetaRating.push({
        playerRoleId: role.id,
        position: role.position,
        metaRating: metaRatingForRole?.metaRating?.toFixed(1),
        metaRatingZeroChem: metaRatingZeroChemForRole?.metaRating?.toFixed(1),

        plusPlus: isRolesPlusOrPlusPlus === "plusPlus",
        plus: isRolesPlusOrPlusPlus === "plus",
        best: bestRoleAndMetaRating?.playerRoleId === role.id,
        chemstyleId: metaRatingForRole?.chemstyleId,
      });
    }
  });

  return (
    <RoleSection
      position={position}
      currentPlayerRoleId={currentPlayerRoleId}
      roles={rolesWithMetaRating}
      handleAction={handleAction}
      isDisabled={isDisabled}
    />
  );
};

export const AllRolesSection = ({
  handleShowAllRoles,
  chemistry,
  possiblePositions,
  currentPlayerRoleId,
  metaRatingsForAllRoles,
  rolesPlus,
  handleAction,
}: AllRolesSectionProps) => {
  const { t } = useTranslation(["players", "common"]);
  return (
    <GridBox
      title={t("detailed.roles.allRoles")}
      className="col-span-4 touch-device:min-h-[560px]"
    >
      <div className="grid grid-cols-4 px-4 pb-3 gap-3 justify-center relative">
        {defaultPositions.map((position) => (
          <RoleSectionWrapper
            key={position.label}
            position={position.label}
            currentPlayerRoleId={currentPlayerRoleId}
            handleAction={handleAction}
            isDisabled={!possiblePositions?.includes(position.label)}
            metaRatingsForAllRoles={metaRatingsForAllRoles}
            rolesPlus={rolesPlus}
            chemistry={chemistry}
          />
        ))}
        <div className="absolute bottom-4 right-4">
          <Button
            label={t("common:showLess")}
            onClick={handleShowAllRoles}
            variant="tertiary"
          />
        </div>
      </div>
    </GridBox>
  );
};
